import React, { useRef, useState } from 'react';
import { SuccessStoryModel } from '../../models';
import { LaurelSuccessCard } from './LaurelSuccessCard';
import { Camera, Sparkles, Download, X, Eye } from 'lucide-react';
import { toPng } from 'html-to-image';
import html2canvas from 'html2canvas';

interface Props {
    stories: SuccessStoryModel[];
    activeSubTab: string;
    selectedYear: string;
    selectedSemester: string;
    selectedSubject: string;
}

export const SuccessStoryExportPoster: React.FC<Props> = ({
    stories,
    activeSubTab,
    selectedYear,
    selectedSemester,
    selectedSubject
}) => {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const posterRef = useRef<HTMLDivElement>(null);

    const getTabTitle = () => {
        if (activeSubTab === 'university') return '영광의 합격자 명단 (대학)';
        if (activeSubTab === 'high_school') return '영광의 합격자 명단 (고등학교)';
        if (activeSubTab === 'grade') return '성적 우수자 명예의 전당';
        if (activeSubTab === 'rank') return '등급 우수자 명예의 전당';
        if (activeSubTab === 'standing') return '등수 우수자 명예의 전당';
        if (activeSubTab === 'improvement') return '성적 향상자 명예의 전당';
        return '명예의 전당';
    };

    const handleDownloadImage = async () => {
        if (!posterRef.current) return;
        try {
            setIsExporting(true);

            // Wait for images/fonts to render cleanly
            await new Promise(resolve => setTimeout(resolve, 350));

            let dataUrl = '';
            try {
                // Primary high-res rendering using html-to-image (supports native oklch / oklab colors)
                dataUrl = await toPng(posterRef.current, {
                    quality: 0.98,
                    pixelRatio: 2,
                    backgroundColor: '#090d16',
                    cacheBust: true,
                });
            } catch (err) {
                console.warn('html-to-image fallback to html2canvas:', err);
                const canvas = await html2canvas(posterRef.current, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    backgroundColor: '#090d16',
                    logging: false,
                    onclone: (clonedDoc) => {
                        const elements = clonedDoc.getElementsByTagName('*');
                        for (let i = 0; i < elements.length; i++) {
                            const el = elements[i] as HTMLElement;
                            const style = window.getComputedStyle(el);
                            if (style.backgroundColor && (style.backgroundColor.includes('oklch') || style.backgroundColor.includes('oklab'))) {
                                el.style.backgroundColor = '#090d16';
                            }
                            if (style.color && (style.color.includes('oklch') || style.color.includes('oklab'))) {
                                el.style.color = '#ffffff';
                            }
                        }
                    }
                });
                dataUrl = canvas.toDataURL('image/png');
            }

            const link = document.createElement('a');
            const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const filterTag = `${selectedYear !== '전체' ? `${selectedYear}_` : ''}${selectedSubject !== '전체' ? `${selectedSubject}_` : ''}${selectedSemester !== '전체' ? `${selectedSemester}_` : ''}`;
            
            link.download = `정성우학원_명예의전당_${filterTag}${dateStr}.png`;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            console.error('Error generating poster image:', error);
            alert('이미지 생성 중 오류가 발생했습니다.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div>
            {/* Open Preview Modal Button */}
            <button
                type="button"
                onClick={() => setIsPreviewOpen(true)}
                disabled={stories.length === 0}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 px-4 py-2.5 rounded-full text-xs sm:text-sm font-black transition-all flex items-center gap-1.5 shadow-md shadow-purple-200 active:scale-95 disabled:opacity-50 cursor-pointer"
                title="현재 필터링된 실적 목록을 포스터 이미지로 미리보고 다운로드"
            >
                <Camera size={16} />
                <span>스샷용 포스터 미리보기</span>
            </button>

            {/* Poster Preview Modal */}
            {isPreviewOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-slate-900 border border-slate-700/80 rounded-3xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden text-white">
                        
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/90 backdrop-blur shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                    <Eye size={18} />
                                </div>
                                <div>
                                    <h3 className="text-base font-black text-white flex items-center gap-2">
                                        <span>정성우학원 명예의 전당 포스터 미리보기</span>
                                        <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                                            {stories.length}건
                                        </span>
                                    </h3>
                                    <p className="text-xs text-slate-400 font-medium">
                                        아래 완성된 포스터 미리보기를 확인하고 다운로드 버튼을 눌러 PNG 이미지로 저장하세요.
                                    </p>
                                </div>
                            </div>

                            {/* Modal Header Actions */}
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleDownloadImage}
                                    disabled={isExporting}
                                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 shadow-lg shadow-indigo-600/30 active:scale-95 disabled:opacity-50"
                                >
                                    <Download size={15} />
                                    <span>{isExporting ? '이미지 생성 중...' : '고화질 PNG 다운로드'}</span>
                                </button>
                                <button
                                    onClick={() => setIsPreviewOpen(false)}
                                    className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition-all"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body - Scrollable Poster Content */}
                        <div className="flex-1 overflow-auto p-6 md:p-8 bg-slate-950/60 flex justify-center">
                            <div
                                ref={posterRef}
                                className="w-[1100px] bg-slate-950 text-white p-10 md:p-12 space-y-10 rounded-3xl border border-white/10 shadow-2xl relative overflow-hidden shrink-0 my-auto"
                                style={{ backgroundColor: '#090d16' }}
                            >
                                {/* Header Banner */}
                                <div className="text-center space-y-4 relative z-10 border-b border-slate-800 pb-10">
                                    <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-indigo-500/10 text-indigo-300 font-black text-xs uppercase tracking-widest border border-indigo-500/20 shadow-inner">
                                        <Sparkles size={16} className="text-indigo-400" />
                                        <span>JUNG SUNG WOO ACADEMY HALL OF FAME</span>
                                    </div>

                                    <h1 className="text-4xl font-black text-white tracking-tight leading-snug">
                                        {selectedYear !== '전체' && `${selectedYear}년 `}
                                        {selectedSemester !== '전체' && `${selectedSemester} `}
                                        {selectedSubject !== '전체' && `${selectedSubject} `}
                                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-violet-300 to-amber-300 ml-2">
                                            {getTabTitle()}
                                        </span>
                                    </h1>

                                    <p className="text-slate-400 font-medium text-base">
                                        정성우학원 수강생들이 땀방울로 이뤄낸 자랑스러운 영광의 기록입니다.
                                    </p>
                                </div>

                                {/* Cards Grid */}
                                <div className="grid grid-cols-3 gap-6 relative z-10 px-2">
                                    {stories.map(story => (
                                        <div key={story.id} className="w-full">
                                            <LaurelSuccessCard story={story} />
                                        </div>
                                    ))}
                                </div>

                                {/* Footer Branding Bar */}
                                <div className="flex items-center justify-between pt-8 border-t border-slate-800 text-slate-400 text-xs font-bold px-2 relative z-10">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-black text-sm shadow-md">
                                            JS
                                        </div>
                                        <div>
                                            <p className="text-white text-sm font-black">정성우학원</p>
                                            <p className="text-[11px] text-slate-400 font-medium">수학 · 과학 · 국어 · 영어 전문</p>
                                        </div>
                                    </div>

                                    <div className="text-right space-y-0.5">
                                        <p className="text-slate-400 font-semibold">발행일자: {new Date().toLocaleDateString('ko-KR')}</p>
                                        <p className="text-[10px] text-slate-500 uppercase tracking-widest">Official Verified Achievement Poster</p>
                                    </div>
                                </div>

                                {/* Background Decorative Glow */}
                                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/10 blur-[150px] rounded-full pointer-events-none"></div>
                                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/10 blur-[150px] rounded-full pointer-events-none"></div>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};
